import React, { useEffect, useState } from "react";
import { post } from "../../../lib/api";

// Use the challenge prop if provided, otherwise fallback to a default
const SpellingBeeEditComponent = ({ game_id, date, onCancel, challenge, onSaved }) => {
  const CURRENT_SELECTED_CHALLENGE = challenge;
  const [challengeDict, setChallengeDict] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editWords, setEditWords] = useState("");
  const [previewWords, setPreviewWords] = useState(null);

  useEffect(() => {
    if (!game_id || !date) return;
    setLoading(true);
    setError(null);
    const url = `${process.env.REACT_APP_CMC_API}/statistics?method=getDailyChallengeComplex&gameid=${game_id}&date=${date}`;
    fetch(url, {
      headers: { Connection: "close" },
      credentials: "include",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch complex challenge data");
        return response.json();
      })
      .then((json) => {
        if (json && json.challenge_name && Array.isArray(json.challenge_name)) {
          const dict = {};
          for (let i = 0; i < json.challenge_name.length; i++) {
            const name = json.challenge_name[i];
            if (!dict[name]) {
              dict[name] = {
                game_id: json.game_id?.[i],
                challenge_name: name,
                variants: {},
              };
            }
            const variant_name = json.variant_name?.[i];
            const variant_id = json.variant_id?.[i];
            const challenge_data = json.challenge_data?.[i];

            if (variant_name) {
              dict[name].variants[variant_name] = {
                variant_id: variant_id,
                challenge_data: challenge_data,
              };
            }
          }
          setChallengeDict(dict);
        } else {
          setChallengeDict(null);
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [game_id, date]);

  // Keep editValues in sync with the current challenge data
  useEffect(() => {
    if (challengeDict && challengeDict[CURRENT_SELECTED_CHALLENGE]) {
      const challenge = challengeDict[CURRENT_SELECTED_CHALLENGE];
      setEditWords(challenge.variants?.words?.challenge_data || "");
    }
  }, [challengeDict, CURRENT_SELECTED_CHALLENGE]);

  // Get the current challenge data
  const selectedChallenge = challengeDict?.[CURRENT_SELECTED_CHALLENGE];
  const lettersData = selectedChallenge?.variants?.letters?.challenge_data;
  const wordsData = selectedChallenge?.variants?.words?.challenge_data;

  // Use previewValue if set, otherwise use the current challenge data
  const displayLetters = lettersData;
  const displayWordsRaw = previewWords !== null ? previewWords : wordsData;

  // Parsing letters
  const [centerLetter, ...otherLetters] = (displayLetters || " | ").split('|').map(s => s.split(',')).flat();

  // Parsing words
  const words = displayWordsRaw ? displayWordsRaw.split(/, ?/).map(w => w.trim()).filter(Boolean) : [];

  // Preview changes by updating the previewData
  const handlePreview = () => {
    setPreviewWords(editWords);
  };

  // Save changes (send to backend and update local state)
  const handleSave = async () => {
    if (!challengeDict || !challengeDict[CURRENT_SELECTED_CHALLENGE]) {
        alert("No challenge selected or data is missing.");
        return;
    }

    const selected = challengeDict[CURRENT_SELECTED_CHALLENGE];
    const wordsVariantId = selected.variants?.words?.variant_id || 2; // Default to 2 for words

    try {
      // Save words
      const wordsResult = await post({
        method: "addChallenge",
        game_id,
        challenge_id: CURRENT_SELECTED_CHALLENGE,
        data: editWords,
        variant_id: wordsVariantId,
        variant_name: "words",
      }, "/challenges");

      if (wordsResult.error) {
        alert("Error saving words: " + wordsResult.error);
        return;
      }

      // Update local state only if both successful
      setChallengeDict(prevDict => {
          const newDict = JSON.parse(JSON.stringify(prevDict)); // Deep copy to ensure re-render
          const challengeToUpdate = newDict[CURRENT_SELECTED_CHALLENGE];

          challengeToUpdate.variants.words = {
              ...(challengeToUpdate.variants.words || {}),
              variant_id: wordsVariantId,
              challenge_data: editWords
          };

          return newDict;
      });

      setPreviewWords(null);
      if (onCancel) onCancel();
      if (typeof onSaved === 'function') onSaved();
    } catch (err) {
      alert("Error saving challenge: " + err.message);
    }
  };

  const handleCancel = () => {
    setPreviewWords(null);
    if(onCancel) onCancel();
  };


  if (loading) return <div>Loading complex challenge data...</div>;
  if (error) return <div style={{ color: 'red' }}>Error: {error}</div>;

  return (
    <div className="p-3 rounded-xl mt-2">
      <div className="mt-2 space-y-2">
          {displayLetters ? (
          <div className="p-2 rounded-md bg-gray-50 dark:bg-neutral-800 border dark:border-neutral-700 text-sm">
            <p><strong>Center Letter:</strong> {centerLetter}</p>
            <p><strong>Other Letters:</strong> {otherLetters.join(', ')}</p>
          </div>
        ) : (
          <div className="text-sm text-neutral-500">Not found or no data for letters.</div>
        )}
        <div>
          <h4 className="font-bold text-sm mb-1">Words ({words.length})</h4>
          {words.length > 0 ? (
            <div className="max-h-32 overflow-y-auto border p-2 rounded-md bg-gray-50 dark:bg-neutral-800 text-xs dark:border-neutral-700">
              {words.join(', ')}
            </div>
          ) : (
            <div className="text-xs text-neutral-500">No words found.</div>
          )}
        </div>
      </div>
      {/* Edit section */}
      <div className="mt-4 flex flex-col gap-2">
        <div>
            <label className="font-semibold text-sm">Words:</label>
            <textarea
            className="w-full p-2 border border-gray-300 dark:border-neutral-600 rounded-md bg-[#ffffff] dark:bg-gray-800 dark:text-white text-xs font-mono min-h-[120px] shadow-sm"
            value={editWords}
            onChange={e => setEditWords(e.target.value)}
            />
        </div>
        <div className="flex gap-2 mt-2">
          <button
            className="bg-gray-50 hover:bg-gray-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 transition-colors shadow-none p-2 min-w-0 rounded-md flex items-center justify-center"
            title="Preview Changes"
            onClick={handlePreview}
          >
            <svg width="20" height="20" fill="none" stroke="#64748b" strokeWidth="2" viewBox="0 0 24 24">
              <path d="M1 12C3.5 7 8.5 4 12 4s8.5 3 11 8c-2.5 5-7.5 8-11 8s-8.5-3-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
          <button
            className="bg-gray-50 hover:bg-gray-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 transition-colors shadow-none p-2 min-w-0 rounded-md flex items-center justify-center"
            title="Save Changes"
            onClick={handleSave}
          >
            <svg width="20" height="20" fill="none" stroke="#22c55e" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7"/></svg>
          </button>
          <button
            className="bg-gray-50 hover:bg-gray-200 dark:bg-neutral-700 dark:hover:bg-neutral-600 transition-colors shadow-none p-2 min-w-0 rounded-md flex items-center justify-center"
            title="Cancel"
            onClick={handleCancel}
          >
            <svg width="20" height="20" fill="none" stroke="#F8A5A5" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SpellingBeeEditComponent; 